/**
 * Verifier-thrown error hierarchy, extracted from `s2s-verify.ts` so
 * peer types in `../types/` (e.g. `JtiReplayed`) can extend the base
 * without importing the full verifier module (which would form a
 * cycle: verifier → types/jti-replayed → verifier).
 *
 * Public surface (re-exported from `./verify` + the package root) is
 * unchanged; this is a pure refactor of module-load order.
 */
export class S2SVerifyError extends Error {
  constructor(message: string, cause?: unknown) {
    super(message);
    this.name = "S2SVerifyError";
    if (cause !== undefined) (this as { cause?: unknown }).cause = cause;
  }
}

export class S2STokenInvalid extends S2SVerifyError {
  constructor(message = "token_invalid", cause?: unknown) {
    super(message, cause);
    this.name = "S2STokenInvalid";
  }
}

export class S2STokenExpired extends S2SVerifyError {
  constructor(message = "token_expired", cause?: unknown) {
    super(message, cause);
    this.name = "S2STokenExpired";
  }
}

export class S2SIssuerMismatch extends S2SVerifyError {
  constructor(message = "issuer_mismatch", cause?: unknown) {
    super(message, cause);
    this.name = "S2SIssuerMismatch";
  }
}

export class S2SAudienceMismatch extends S2SVerifyError {
  constructor(message = "audience_mismatch", cause?: unknown) {
    super(message, cause);
    this.name = "S2SAudienceMismatch";
  }
}

/**
 * The jti-replay store could not answer, so the verifier could not decide
 * whether this jti is fresh.
 *
 * ⚠️ NOT A REJECTION OF THE TOKEN. Nothing about the presented token is known
 * to be wrong; an external dependency is down. The correct caller response is
 * RETRY; the correct response to `S2STokenInvalid` is "your token was bad, do
 * not retry it". A caller cannot make that choice unless the two are distinct
 * classes, which is the entire reason this one exists.
 *
 * WHAT IT REPLACES. `verifyS2SToken` used to `await opts.replayStore.reserve(
 * jti, ttlMs)` bare, so a raw driver error (an ioredis `ConnectionError`, a
 * `MaxRetriesPerRequestError`) escaped the verifier untouched. Downstream
 * `catch (err)` blocks that map by class — including this package's own
 * synthetic consumer — fell through to their default arm and reported a Redis
 * blip as `S2STokenInvalid` / HTTP 401, i.e. an infra outage indistinguishable
 * from a forged token, on the exact typed-error axis parity-fence fixtures
 * 04/06/07 exist to fence.
 *
 * This package's OWN sibling path already got it right — `gateway-hmac/bound.ts`
 * catches around `replayStore.reserve` and returns a typed
 * `REPLAY_STORE_ERROR` — so the S2S verifier was the outlier inside a single
 * package, not a missing idea. Go (`ReplayStoreUnavailableError`) and Python
 * (`ReplayStoreUnavailableError`) are fixed in the same change: this is a class
 * across three ports.
 *
 * FAIL-CLOSED. Like `bound.ts`, this is thrown rather than swallowed: an
 * unreachable store means the verifier cannot prove the jti is unseen, so it
 * must not return success.
 */
export class S2SReplayStoreUnavailable extends S2SVerifyError {
  constructor(message = "replay_store_unavailable", cause?: unknown) {
    super(message, cause);
    this.name = "S2SReplayStoreUnavailable";
  }
}
